/**
 * TierRouteGate — Route-level gate that checks subscription tier and module toggles.
 *
 * Works alongside the existing ModuleRouteGate (RBAC-based).
 * If the org's tier is too low, renders UpgradeCard instead of the route.
 * If the module is toggled off by the admin, redirects to home.
 *
 * Usage:
 *   <TierRouteGate module="accounting">
 *     <AccountingPage />
 *   </TierRouteGate>
 */

import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { useTierGate } from "@/hooks/useTierGate";
import { TierGate } from "@/components/ui/TierGate";
import type { ToggleableModule } from "@/lib/tier-config";

interface TierRouteGateProps {
  module: ToggleableModule;
  children: ReactNode;
  /** Where to redirect if admin toggled the module off (default: "/") */
  toggledOffRedirect?: string;
}

export function TierRouteGate({
  module,
  children,
  toggledOffRedirect = "/",
}: TierRouteGateProps) {
  const { hasAccess, tierQualifies, toggleEnabled, isLoading } = useTierGate(module);

  if (isLoading) {
    return <ForgePageLoader />;
  }

  // Admin toggled it off — redirect silently
  if (tierQualifies && !toggleEnabled) {
    return <Navigate to={toggledOffRedirect} replace />;
  }

  // Tier too low — show the upgrade card (TierGate handles this)
  if (!tierQualifies) {
    return (
      <TierGate module={module}>
        {children}
      </TierGate>
    );
  }

  // All good
  return <>{children}</>;
}
